import {
  QUERY_MATCH_RANK_NONE,
  QUERY_MATCH_RANK_SUBSTRING,
  matchQueryValues,
} from "../js/query/match.js";

const DEFAULT_SUGGESTION_LIMIT = 12;

function normalizedExactValue(value) {
  return String(value ?? "").trim().toLowerCase();
}

export function queryInputSuggestionExactMatchesValue(rawValue, values) {
  const needle = normalizedExactValue(rawValue);
  if (!needle) {
    return false;
  }
  return (Array.isArray(values) ? values : []).some(
    (value) => normalizedExactValue(value) === needle,
  );
}

export function rankQueryInputSuggestion({
  label,
  insertText,
  exactMatchInput = "",
  exactMatchValues = [],
  previewText = "",
  score = 0,
}) {
  const normalizedExactMatchValues = Array.isArray(exactMatchValues) ? exactMatchValues : [];
  const hasExactInputMatch = queryInputSuggestionExactMatchesValue(
    exactMatchInput,
    normalizedExactMatchValues,
  );
  const suggestionMatch = exactMatchInput
    ? matchQueryValues(
        [...normalizedExactMatchValues, label, insertText, previewText],
        exactMatchInput,
      )
    : null;
  return {
    exactMatchValues: normalizedExactMatchValues,
    matchRank: suggestionMatch?.rank || QUERY_MATCH_RANK_NONE,
    matchMode: suggestionMatch?.matchMode || "",
    matchedValue: suggestionMatch?.matchedValue || "",
    windowStart: suggestionMatch?.windowStart ?? null,
    windowEnd: suggestionMatch?.windowEnd ?? null,
    score:
      score +
      (hasExactInputMatch ? 1000 : 0) +
      (suggestionMatch?.rank || 0) * 25 +
      Math.max(0, Math.min(Number(suggestionMatch?.score) || 0, 100000)) / 10000,
  };
}

function candidateRank(candidate) {
  return Number.parseInt(candidate?.matchRank, 10) || QUERY_MATCH_RANK_NONE;
}

function candidateKey(candidate) {
  return `${candidate?.replaceStart}:${candidate?.replaceEnd}:${candidate?.insertText}`;
}

function compareCandidates(left, right) {
  return (
    (right?.score || 0) - (left?.score || 0) ||
    String(left?.label || "").localeCompare(String(right?.label || "")) ||
    (left?._ordinal || 0) - (right?._ordinal || 0)
  );
}

function insertBoundedCandidate(candidates, candidate, limit) {
  const key = candidateKey(candidate);
  const duplicateIndex = candidates.findIndex((entry) => candidateKey(entry) === key);
  if (duplicateIndex >= 0) {
    if (compareCandidates(candidate, candidates[duplicateIndex]) >= 0) {
      return;
    }
    candidates.splice(duplicateIndex, 1);
  }
  candidates.push(candidate);
  candidates.sort(compareCandidates);
  if (candidates.length > limit) {
    candidates.length = limit;
  }
}

export function selectBoundedQueryInputCandidates(
  sourceCandidates,
  requestedLimit = DEFAULT_SUGGESTION_LIMIT,
) {
  const parsedLimit = Number.parseInt(requestedLimit, 10);
  const limit = Number.isInteger(parsedLimit) && parsedLimit > 0
    ? parsedLimit
    : DEFAULT_SUGGESTION_LIMIT;
  const matching = [];
  const fallback = [];
  let ordinal = 0;

  for (const sourceCandidate of sourceCandidates) {
    if (!sourceCandidate) {
      continue;
    }
    const candidate = sourceCandidate._ordinal == null
      ? { ...sourceCandidate, _ordinal: ordinal }
      : sourceCandidate;
    ordinal += 1;
    insertBoundedCandidate(
      candidateRank(candidate) >= QUERY_MATCH_RANK_SUBSTRING ? matching : fallback,
      candidate,
      limit,
    );
  }

  return matching.length ? matching : fallback;
}
